import { useEffect, useRef, useState } from 'react';
import { api } from './api.js';
export type AddressSuggestion = {
  email: string;
  name: string;
  source: 'contact' | 'domain' | 'sender';
};
export const sourceLabel = (source: AddressSuggestion['source']) =>
  source === 'contact' ? 'Contact' : source === 'domain' ? 'Domaine' : 'Correspondant';

// The composer keeps its comma separated text field; suggestions only complete the last address.
const lastToken = (value: string) => value.split(/[,;]/).pop() ?? '';
const replaceLast = (value: string, email: string) => {
  const parts = value.split(/[,;]/);
  parts[parts.length - 1] = ' ' + email;
  return parts.join(',').replace(/^[\s,]+/, '') + ', ';
};

export function RecipientField({
  id,
  label,
  value,
  disabled,
  autoFocus,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange: (value: string) => void;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false),
    [results, setResults] = useState<AddressSuggestion[]>([]),
    [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  // Tab picks a suggestion only once the person browsed them with the arrows; otherwise it leaves
  // the field as usual, so that keyboard users are never held in it.
  const browsed = useRef(false);
  const query = lastToken(value).trim().toLowerCase();
  const chosen = value
    .split(/[,;]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const choices = results.filter((r) => !chosen.includes(r.email) || r.email === query);
  useEffect(() => {
    if (!open || query.length < 1) {
      setResults([]);
      return;
    }
    const abort = new AbortController();
    const timer = setTimeout(() => {
      void api<AddressSuggestion[]>('/suggestions?q=' + encodeURIComponent(query), {
        signal: abort.signal,
      })
        .then((rows) => {
          if (!abort.signal.aborted) {
            setResults(rows);
            setActive(0);
          }
        })
        .catch(() => {
          if (!abort.signal.aborted) setResults([]);
        });
    }, 180);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [query, open]);
  const pick = (email: string) => {
    onChange(replaceLast(value, email));
    setResults([]);
    setOpen(false);
    input.current?.focus();
  };
  const visible = open && choices.length > 0;
  return (
    <div className="compose-field">
      <label htmlFor={id}>{label}</label>
      <div className="recipient-input">
        <input
          id={id}
          ref={input}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={visible}
          aria-controls={id + '-suggestions'}
          aria-activedescendant={visible ? id + '-option-' + active : undefined}
          disabled={disabled}
          autoFocus={autoFocus}
          value={value}
          placeholder={label === 'À' ? 'Destinataires' : undefined}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            browsed.current = false;
            onChange(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (!visible) return;
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              browsed.current = true;
              setActive((n) =>
                Math.max(0, Math.min(choices.length - 1, n + (e.key === 'ArrowDown' ? 1 : -1))),
              );
            } else if (e.key === 'Enter' || (e.key === 'Tab' && browsed.current)) {
              e.preventDefault();
              pick(choices[active].email);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
            }
          }}
        />
        {visible && (
          <div
            className="recipient-suggestions"
            id={id + '-suggestions'}
            role="listbox"
            aria-label={`Suggestions pour ${label}`}
          >
            {choices.map((r, i) => (
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                id={id + '-option-' + i}
                key={r.email}
                className={i === active ? 'highlighted' : ''}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(r.email)}
              >
                <span>
                  <strong>{r.name || r.email}</strong>
                  <small>{r.email}</small>
                </span>
                <small>{sourceLabel(r.source)}</small>
              </button>
            ))}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
